<script setup lang="ts">
import { Analytics } from "@vercel/analytics/nuxt";
import { SpeedInsights } from "@vercel/speed-insights/nuxt";

// GSAP will be imported dynamically on client side
import { useHead, useNuxtApp, useRoute, useRouter } from "nuxt/app";
import { scrollToHashOrTop } from "~/utils/scrollToHash";

const nuxtApp = useNuxtApp();
const router = useRouter();
const route = useRoute();

useHead({
  htmlAttrs: {
    lang: "en",
  },
  meta: [{ name: "viewport", content: "width=device-width, initial-scale=1" }],
  link: [
    { rel: "icon", type: "image/png", href: "/mbt-fav.png" },
    { rel: "icon", type: "image/png", sizes: "32x32", href: "/mbt-fav.png" },
    { rel: "icon", type: "image/png", sizes: "16x16", href: "/mbt-fav.png" },
    { rel: "apple-touch-icon", sizes: "180x180", href: "/mbt-fav.png" },
    { rel: "manifest", href: "/site.webmanifest" },
  ],
});

// Initialize GSAP composable
const { ScrollTrigger, ScrollSmoother, loadGSAP, isLoaded } = useGSAP();

// Kill all scroll trigger instances before redirecting
router.beforeEach(async () => {
  if (!process.client) return;

  try {
    await loadGSAP();

    if (isLoaded.value && ScrollTrigger.value) {
      // Kill all ScrollTrigger instances
      ScrollTrigger.value.getAll().forEach((trigger) => {
        try {
          trigger.kill();
        } catch (error) {
          console.warn("Failed to kill ScrollTrigger:", error);
        }
      });
    }

    if (isLoaded.value && ScrollSmoother.value) {
      // Kill ScrollSmoother instance
      const smoother = ScrollSmoother.value.get();
      if (smoother) {
        try {
          smoother.kill();
        } catch (error) {
          console.warn("Failed to kill ScrollSmoother:", error);
        }
      }
    }
  } catch (error) {
    console.warn("GSAP cleanup failed:", error);
  }
});

// Handle Suspense resolution (e. g., async pages)
nuxtApp.hook("page:finish", async () => {
  if (!process.client) return;

  // Honour URL hashes (e.g. /terms-and-conditions#cancellation-by-you)
  // instead of always forcing the page back to the top.
  scrollToHashOrTop();

  // Defer ScrollSmoother / ScrollTrigger work past the LCP window.
  const runAfterLcp = () => {
    void (async () => {
      try {
        await loadGSAP();

        if (!isLoaded.value) {
          console.warn("GSAP not loaded, skipping page:finish animations");
          return;
        }

        const { gsap, ScrollTrigger, ScrollSmoother } = useGSAP();

        setTimeout(() => {
          try {
            if (ScrollTrigger.value) {
              ScrollTrigger.value.refresh();
            }
          } catch (error) {
            console.warn("ScrollTrigger refresh failed:", error);
          }
        }, 750);

        // Skip ScrollSmoother on homepage — it contends with hero LCP / map.
        if (route.path === "/" || route.name === "index") {
          return;
        }

        if (gsap.value) {
          const mm = gsap.value.matchMedia();
          if (route.name !== "newsletters-id") {
            mm.add("(min-width: 992px)", () => {
              setTimeout(() => {
                try {
                  if (ScrollSmoother.value) {
                    const instance = ScrollSmoother.value.get();
                    if (instance) instance.kill();

                    scrollToHashOrTop();

                    if (document.body) {
                      const smoother = ScrollSmoother.value.create({
                        smooth: 1,
                        effects: true,
                        smoothTouch: 0.1,
                      });

                      if (smoother) {
                        scrollToHashOrTop({ smoother });
                      }
                    }
                  }
                } catch (error) {
                  console.warn("ScrollSmoother creation failed:", error);
                }
              }, 750);
            });
          }
        }
      } catch (error) {
        console.warn("GSAP page:finish hook failed:", error);
      }
    })();
  };

  if ("requestIdleCallback" in window) {
    requestIdleCallback(runAfterLcp, { timeout: 5000 });
  } else {
    setTimeout(runAfterLcp, 2500);
  }
});
</script>

<template>
  <Html lang="en">
    <Body>
      <NuxtLayout>
        <NuxtPage />
      </NuxtLayout>
      <SpeedInsights />
      <Analytics />
    </Body>
  </Html>
</template>
